/**
 * Sheet 40 — Settings › Organization (A.2, A.4, T.1; FR-004, FR-013; callouts
 * 2–5). The Header parameters half of the sheet is `HeaderParameters.tsx`;
 * both render inside `SettingsLayout` (callout 1), within the app shell, which
 * owns the desktop-only notice and the one `main` (mission 6 WP05 T033).
 *
 * Every field is read-only (§7.2 read-only treatment): at GA there is one
 * identity per tenant and no user management, so nothing here is edited yet.
 * The tenant ID is immutable, in mono, with a copy control — it is what a
 * customer quotes in support and data requests (callout 3). The terms record
 * names the organization, not a person, as the accepting party (callout 5,
 * T.1). Verification status mirrors the operator step. The Security card
 * regenerates backup codes behind a current authenticator code (A.4): the
 * codes live in this component's state only and are cleared when the dialog
 * closes, so reopening never shows the previous set.
 *
 * The dialog is built inline (§7.13: h2, 24px body, ghost Cancel then the
 * primary, Esc closes, focus returns to the trigger) because the shadcn Dialog
 * is not vendored yet (components/ui/README.md).
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { Link } from 'react-router-dom'

import { PROBLEM, isProblem, regenerateBackupCodes, type Tenant } from '@/lib/auth'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/components/ui/cn'
import { useTenant } from '@/components/trellis/AuthGate'
import { BackupCodes } from '@/components/trellis/BackupCodes'
import { DisabledReason, failureCopy } from '../AuthLayout'
import { SettingsLayout } from './SettingsLayout'

const TOTP = /^\d{6}$/

/** "Sep 15, 2026" — fixed locale and zone so the record reads the same everywhere. */
export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

export default function Organization() {
  const { data: tenant } = useTenant()

  return (
    <SettingsLayout>
      <h1 className="text-h1 text-navy-950">Organization</h1>
      {tenant && <OrganizationCards tenant={tenant} />}
    </SettingsLayout>
  )
}

// ---------------------------------------------------------------------------

function OrganizationCards({ tenant }: { tenant: Tenant }) {
  const [copied, setCopied] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  async function copyId() {
    try {
      await navigator.clipboard.writeText(tenant.id)
      setCopied(true)
    } catch {
      // No clipboard permission: the field is selectable.
    }
  }

  function closeDialog() {
    setDialogOpen(false)
    triggerRef.current?.focus()
  }

  const verified = tenant.verified_at !== null && tenant.status !== 'pending_verification'

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>As entered at signup. The legal name is the party bound by the terms.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-5">
          <ReadOnlyField label="Legal name" value={tenant.legal_name} />
          <ReadOnlyField label="Contact email" value={tenant.contact_email} />
          <ReadOnlyField
            label="Tenant ID"
            value={tenant.id}
            mono
            help="Immutable; quote in support and data requests."
            action={
              <Button variant="secondary" size="sm" onClick={() => void copyId()} aria-label="Copy tenant ID">
                {copied ? 'Copied' : 'Copy'}
              </Button>
            }
          />
          <ReadOnlyField
            label="Reports to the credit bureaus today"
            value={tenant.reports_today ? 'Yes' : 'No'}
            help="Your answer at signup. Editable with user management in a later mission."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Terms</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="text-body text-navy-950">
            Version <span className="font-mono">{tenant.terms_version}</span>, accepted{' '}
            {formatDate(tenant.terms_accepted_at)} on behalf of {tenant.legal_name}. Trellis records the
            organization, not a person, as the accepting party.
          </p>
          <p className="text-body-sm">
            <Link to="/legal/terms" className="text-[var(--color-link)] hover:underline">
              Read the current terms
            </Link>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verification</CardTitle>
          <CardDescription>A Bloom operator verifies every new credit data reporter before the app opens.</CardDescription>
        </CardHeader>
        <CardContent>
          {verified && tenant.verified_at ? (
            <StatusBadge kind="success">Verified {formatDate(tenant.verified_at)}</StatusBadge>
          ) : (
            <StatusBadge kind="pending">Waiting for verification</StatusBadge>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Security</CardTitle>
          <CardDescription>
            Eight single-use backup codes let you log in if you lose your authenticator. Regenerating replaces
            every code you have.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button ref={triggerRef} variant="secondary" onClick={() => setDialogOpen(true)}>
            Regenerate backup codes
          </Button>
        </CardContent>
      </Card>

      {dialogOpen && <RegenerateDialog onClose={closeDialog} />}
    </>
  )
}

// ---------------------------------------------------------------------------

interface ReadOnlyFieldProps {
  label: string
  value: string
  mono?: boolean
  help?: string
  action?: ReactNode
}

/** §7.2 read-only: navy-50 field, lock icon at right, value still selectable. */
function ReadOnlyField({ label, value, mono = false, help, action }: ReadOnlyFieldProps) {
  const id = useId()
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Input id={id} readOnly value={value} className={cn('pr-9', mono && 'font-mono')} aria-describedby={help ? `${id}-help` : undefined} />
          <Lock aria-hidden className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-navy-500" />
        </div>
        {action}
      </div>
      {help && (
        <p id={`${id}-help`} className="text-caption text-navy-600">
          {help}
        </p>
      )}
    </div>
  )
}

/** §2.6 status badge: success (Verified) or pending (info family). */
function StatusBadge({ kind, children }: { kind: 'success' | 'pending'; children: ReactNode }) {
  return (
    <span
      className={cn(
        'inline-flex h-[22px] items-center rounded-sm border px-2 text-caption font-medium',
        kind === 'success'
          ? 'border-[var(--color-success-200)] bg-[var(--color-success-50)] text-[var(--color-success-700)]'
          : 'border-[var(--color-info-200)] bg-[var(--color-info-50)] text-[var(--color-info-800)]',
      )}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------

function RegenerateDialog({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const titleId = useId()
  const codeId = useId()
  const reasonId = useId()
  const panelRef = useRef<HTMLDivElement>(null)

  // Esc closes; Tab stays inside the panel (§7.13).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key !== 'Tab' || !panelRef.current) return
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const reason = TOTP.test(code) ? null : 'Generate new codes is unavailable until you enter the 6-digit code.'
  const canSubmit = reason === null && !submitting

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await regenerateBackupCodes({ code })
      setCode('')
      setCodes(res.backup_codes)
    } catch (err) {
      setError(
        isProblem(err, PROBLEM.INVALID_CODE)
          ? 'That code is not right. Codes change every 30 seconds; enter the one showing now.'
          : failureCopy(err),
      )
    } finally {
      setSubmitting(false)
    }
  }

  function done() {
    setCodes([])
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--overlay)] p-8">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-[480px] rounded-lg bg-white p-6 shadow-[var(--shadow-lg)]"
      >
        <h2 id={titleId} className="text-h2 text-navy-950">
          Regenerate backup codes
        </h2>

        {codes.length > 0 ? (
          <div className="mt-4">
            <BackupCodes codes={codes} onContinue={done} continueLabel="Done" />
          </div>
        ) : (
          <form className="mt-4 flex flex-col gap-5" onSubmit={(e) => void onSubmit(e)} noValidate>
            <p className="text-body text-navy-700">
              Your previous codes stop working as soon as new ones are generated. Confirm with your
              authenticator to continue.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={codeId}>Code from your authenticator app</Label>
              <Input
                id={codeId}
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                className="max-w-[160px] font-mono"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                aria-invalid={error ? true : undefined}
              />
            </div>
            {error && (
              <Alert variant="blocked">
                <AlertDescription>
                  <p>{error}</p>
                </AlertDescription>
              </Alert>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex justify-end gap-3">
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit}
                  aria-busy={submitting || undefined}
                  aria-describedby={reason ? reasonId : undefined}
                >
                  Generate new codes
                </Button>
              </div>
              {reason && (
                <div className="text-right">
                  <DisabledReason id={reasonId}>{reason}</DisabledReason>
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
